import { Box, useMediaQuery, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { isOtpActive } from '!core/redux/dbConfigurations';
import { ButtonMain } from '»components/atoms/Buttons';
import { TrlSpacer } from '»components/atoms/TrlSpacer';
import { Otp } from '»components/Auth';
import {
  PrimarySimulationCharges,
  PrimarySimulationChargesOrder,
} from '»components/molecules/PrimarySimulationCharges';
import { TrlDisclaimer } from '»components/molecules/TrlDisclaimer';
import { TrlErrorContainer } from '»components/molecules/TrlErrorContainer';
import { TrlPrimaryFund } from '»components/molecules/TrlPrimaryFund';
import { TrlPrimaryInputData } from '»components/molecules/TrlPrimaryInputData';
import {
  TrlPrimaryMarketIssue,
  TrlPrimaryMarketIssueMobile,
} from '»components/molecules/TrlPrimaryMarketIssue';
import { useCurrentPage } from '»utils/customHooks';
import { TrlFormattedNumberPlain } from '»utils/i18n';

import { handleBack, isLastStep } from './TrlInsertOrderPrimary.steps.helper';
import {
  BoxContainerStyled,
  DisclaimerContainerStyled,
  RightContainer,
  SimulationChargesBox,
  SimulationContainer,
  StepHeaderLabelStyled,
  StepperBoxIn,
} from './TrlInsertOrderPrimary.styled';

export const GetStepContent = ({
  step,
  steps,
  validRequest,
  invalidReason,
  orderStatus,
  activeStep,
  location,
  setActiveStep,
  orderComissions,
  symbol,
  currency,
  ...rest
}) => {
  const {
    isSubmitting,
    dirty,
    values: { inputQuantity, inputPrice, validationCode },
    setFieldValue,
  } = rest;

  const { edit, dataItem } = location.state;

  const mobileView = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const tabletView = useMediaQuery((theme) =>
    theme.breakpoints.between('sm', 'lg'),
  );
  const currentTheme = useTheme();
  const lastStep = isLastStep({ activeStep, steps });

  const [feedbackMsg, setFeedbackMsg] = useState(null);

  const { isFundsListPage } = useCurrentPage();

  const useOtpForPrimaryMarket = useSelector((state) =>
    isOtpActive(state, 'ENFORCE_OTP_PRIMARY_ORDER'),
  );
  const useOtpForFundsPrimary = useSelector((state) =>
    isOtpActive(state, 'ENFORCE_OTP_FUNDS_PRIMARY'),
  );

  const useOtp = isFundsListPage
    ? useOtpForFundsPrimary
    : useOtpForPrimaryMarket;

  const operationType = isFundsListPage ? '' : 'B';

  const handleGetOptError = (event) => {
    setFeedbackMsg(event);
  };

  const getTitleDetails = () => {
    if (isFundsListPage)
      return <TrlPrimaryFund dataItem={dataItem} edit={edit} />;

    if (mobileView)
      return <TrlPrimaryMarketIssueMobile dataItem={dataItem} edit={edit} />;
    return <TrlPrimaryMarketIssue dataItem={dataItem} edit={edit} />;
  };

  switch (step) {
    case 0:
      return (
        <>
          <StepHeaderLabelStyled>
            <FormattedMessage
              id={`${edit ? 'TRLT_UI-MODIFY_INTENTION' : 'TRLT_UI-INTENTION'}`}
            />
          </StepHeaderLabelStyled>

          <StepperBoxIn>
            {(mobileView || tabletView) && (
              <Box marginTop={0.5}>
                <TrlErrorContainer
                  showContainer={!!(!validRequest && invalidReason)}
                  errorMessage={invalidReason}
                />
              </Box>
            )}

            {getTitleDetails()}

            <TrlSpacer width={currentTheme.spacing(3)} />
            <BoxContainerStyled>
              <DisclaimerContainerStyled>
                {!(mobileView || tabletView) && (
                  <Box marginTop="4px">
                    <TrlErrorContainer
                      showContainer={!!(!validRequest && invalidReason)}
                      errorMessage={invalidReason}
                    />
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                  }}
                >
                  <RightContainer marginTop={2}>
                    <TrlPrimaryInputData
                      {...rest}
                      dataItem={dataItem}
                      edit={edit}
                    />

                    <TrlDisclaimer
                      disclaimerId={
                        isFundsListPage ? 'NEG_FUND_PRIM' : 'NEG_PRIM'
                      }
                      operationType={operationType}
                    />
                  </RightContainer>

                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="end"
                    marginTop={3}
                    marginRight={!(mobileView || tabletView) ? 2 : 0}
                  >
                    {activeStep !== 0 && !lastStep && (
                      <ButtonMain
                        disabled={activeStep === 0}
                        onClick={() => handleBack({ setActiveStep })}
                        variant="secondary"
                        expressionId="TRLT_UI-BACK"
                        mr={2}
                      />
                    )}
                    {!lastStep && (
                      <ButtonMain
                        disabled={lastStep || isSubmitting || !dirty}
                        isLoading={isSubmitting}
                        expressionId="TRLT_UI-NEXT"
                        type="submit"
                        id="submit"
                      />
                    )}
                  </Box>
                </Box>
              </DisclaimerContainerStyled>
            </BoxContainerStyled>
          </StepperBoxIn>
        </>
      );
    case 1: {
      const [{ grossAmount }] = orderComissions;

      const formattedPrice = isFundsListPage
        ? TrlFormattedNumberPlain(Number(dataItem.subscriptionPrice), 2)
        : inputPrice
          ? TrlFormattedNumberPlain(Number(inputPrice), 2)
          : '-';
      const formattedAmount = TrlFormattedNumberPlain(grossAmount, 2);

      return (
        <>
          <StepHeaderLabelStyled>
            <FormattedMessage id="TRLT_UI-SIMULATION" />
          </StepHeaderLabelStyled>
          <StepperBoxIn>
            {(mobileView || tabletView) && (
              <Box marginTop={0.5}>
                <TrlErrorContainer
                  showContainer={
                    !!(!validRequest && invalidReason) || feedbackMsg
                  }
                  errorMessage={invalidReason || feedbackMsg}
                />
              </Box>
            )}
            {!mobileView && !tabletView && (
              <PrimarySimulationCharges dataItem={dataItem} />
            )}
            <Box display="flex" flexDirection="column">
              <SimulationContainer>
                <SimulationChargesBox>
                  {(mobileView || tabletView) && (
                    <PrimarySimulationCharges dataItem={dataItem} />
                  )}
                  {orderComissions && (
                    <PrimarySimulationChargesOrder
                      orderComissions={orderComissions}
                      dataItem={dataItem}
                      {...rest}
                    />
                  )}
                </SimulationChargesBox>
                <DisclaimerContainerStyled>
                  {!(mobileView || tabletView) && (
                    <Box marginTop="4px">
                      <TrlErrorContainer
                        showContainer={!!(!validRequest && invalidReason)}
                        errorMessage={invalidReason}
                      />
                    </Box>
                  )}
                  {useOtp && (mobileView || tabletView) && (
                    <Otp
                      margin={mobileView ? '32px 16px 0' : '32px 0 0 0'}
                      textFieldName="validationCode"
                      onError={handleGetOptError}
                      ordType={isFundsListPage ? 'D' : '1'}
                      symbol={symbol}
                      orderQty={inputQuantity}
                      price={formattedPrice}
                      amount={formattedAmount}
                      currency={currency}
                      currencySymbol={currency}
                      isTighter
                    />
                  )}
                  <TrlDisclaimer
                    disclaimerId={
                      isFundsListPage ? 'SIM_FUND_PRIM' : 'SIM_PRIM'
                    }
                    operationType={operationType}
                  />
                  <Box
                    id="1"
                    dislay="flex"
                    flexDirection="column"
                    marginTop={currentTheme.spacing()}
                  >
                    <Box
                      id="1.2"
                      display="flex"
                      flexDirection="row"
                      justifyContent="end"
                    >
                      {activeStep !== 0 && !lastStep && (
                        <ButtonMain
                          disabled={isSubmitting || activeStep === 0}
                          onClick={() => {
                            handleBack({ setActiveStep });
                            setFieldValue('validationCode', '');
                          }}
                          variant="secondary"
                          expressionId="TRLT_UI-BACK"
                          mr={2}
                        />
                      )}
                      {!lastStep && (
                        <ButtonMain
                          disabled={
                            lastStep ||
                            isSubmitting ||
                            (useOtp && validationCode === '')
                          }
                          isLoading={isSubmitting}
                          expressionId="TRLT_UI-NEXT"
                          type="submit"
                          id="submit"
                        />
                      )}
                    </Box>
                  </Box>
                </DisclaimerContainerStyled>
              </SimulationContainer>
              {useOtp && !mobileView && !tabletView && (
                <Otp
                  margin="16px 0 0 16px"
                  textFieldName="validationCode"
                  onError={handleGetOptError}
                  ordType={isFundsListPage ? 'D' : '1'}
                  symbol={symbol}
                  orderQty={inputQuantity}
                  price={formattedPrice}
                  amount={formattedAmount}
                  currency={currency}
                  currencySymbol={currency}
                  isTighter
                />
              )}
            </Box>
          </StepperBoxIn>
        </>
      );
    }
    case 2:
      return (
        <>
          <StepHeaderLabelStyled>
            <FormattedMessage id="TRLT_UI-CONFIRMATION" />
          </StepHeaderLabelStyled>
          <StepperBoxIn>
            <SimulationChargesBox>
              <PrimarySimulationCharges dataItem={dataItem} />

              <PrimarySimulationChargesOrder
                final
                order={orderStatus}
                orderComissions={orderComissions}
                dataItem={dataItem}
                {...rest}
              />
            </SimulationChargesBox>
            <DisclaimerContainerStyled>
              <TrlDisclaimer
                expressionId="TRLT_UI-ORDER_NUMBER"
                expressionParams={{
                  ordBookKey:
                    orderStatus && orderStatus.length > 0
                      ? orderStatus[0].ordBookKey
                      : '',
                }}
              />
            </DisclaimerContainerStyled>
          </StepperBoxIn>
        </>
      );
    default:
      return 'Unknown step';
  }
};

GetStepContent.propTypes = {
  data: PropTypes.object,
  step: PropTypes.number,
  validRequest: PropTypes.bool,
  invalidReason: PropTypes.string,
  orderStatus: PropTypes.string,
  steps: PropTypes.array,
  activeStep: PropTypes.number,
  location: PropTypes.object,
  setActiveStep: PropTypes.func,
  orderComissions: PropTypes.object,
  symbol: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
};
